/**
 * ContentCard 컴포넌트
 * @description 미디어 아래에 title·metadata 를 세로로 쌓는다. 폭은 부모가 정하고 높이는
 *   내용만큼 차지한다. 배경과 패딩이 없어 담는 쪽이 여백을 정한다. 앱 `shared/ui/ContentCard`
 *   의 웹 포팅 — 누름은 `href`(링크) 또는 `onPress`(버튼)로 받는다.
 * @example
 * <ContentCard title={collab.collabTitle} metadata="Crew • 2d left" href={`/collabs/${id}`}
 *   media={<AppImage uri={collab.collabThumbnailUrl[0]} />} />
 */
import { Media } from "@fanwork-web/design-system";
import Link from "next/link";
import { memo } from "react";

import { contentCardStyles } from "./ContentCard.styles";
import type { ContentCardProps } from "./ContentCard.types";

/** 제목은 두 줄, 곁글은 한 줄에서 끊는다 — 나란히 선 카드들의 아래 선이 제각각이 되지 않게 */
const TITLE_LINES = 2;

const ContentCard = memo<ContentCardProps>(
  ({
    title,
    metadata,
    titleLines = TITLE_LINES,
    fixedTextHeight = false,
    media,
    ratio = "square",
    href,
    onPress,
    onClick,
    accessibilityLabel,
    testID,
  }) => {
    /*
      자리를 못 박을 때 제목은 **늘 두 줄 자리**를 쓴다. 제목이 아예 없는 글에서만 곁글이
      그 두 줄을 가져간다.
    */
    const hasTitle = Boolean(title);
    const titleRows = fixedTextHeight ? TITLE_LINES : titleLines;
    const metadataRows = fixedTextHeight && !hasTitle ? TITLE_LINES : 1;
    const interactive = Boolean(href || onPress);

    const content = (
      <>
        <div className={contentCardStyles.mediaClip}>
          <Media ratio={ratio} slot={media} />
        </div>
        <div className={contentCardStyles.texts({ fixed: fixedTextHeight })}>
          {hasTitle ? (
            <p className={contentCardStyles.title({ lines: titleRows })}>
              {title}
            </p>
          ) : null}
          {metadata ? (
            <p className={contentCardStyles.metadata({ lines: metadataRows })}>
              {metadata}
            </p>
          ) : null}
        </div>
      </>
    );
    const className = contentCardStyles.root({ interactive });

    if (href) {
      return (
        <Link
          href={href}
          onClick={onClick}
          className={className}
          aria-label={accessibilityLabel ?? title}
          data-testid={testID}
          draggable={false}
        >
          {content}
        </Link>
      );
    }
    // 누를 수 없는 카드까지 button 으로 감싸면 접근성 트리에 빈 버튼이 쌓인다
    if (!onPress) {
      return (
        <div className={className} data-testid={testID}>
          {content}
        </div>
      );
    }
    return (
      <button
        type="button"
        className={className}
        onClick={onPress}
        aria-label={accessibilityLabel ?? title}
        data-testid={testID}
      >
        {content}
      </button>
    );
  },
);

ContentCard.displayName = "ContentCard";

export default ContentCard;
